// Key prop gets added from the result of rest operator
// example <tr {...headerGroup.getHeaderGroupProps()} data-testid="sub_table-head">
/* eslint-disable react/jsx-key */

import React, { FC, Fragment, useContext, useEffect } from 'react';
import { useTable, useSortBy, useExpanded, Column, Row, useFlexLayout } from 'react-table';
import clsx from 'clsx';
import * as _ from 'lodash';
import { Carets, CaretDown } from '@td-client-frontend/icons';
import { ReactTableContext } from '../../context';
import { PageSpinner } from '../Spinner';

export type DefaultColumnType = Column<Record<string, any>>[];

interface TableRow {
  row: Row;
  expandedRowClassname?: string;
  expandedCellClassName?: string;
}
interface ReactTable {
  columns: readonly Column<Record<string, any>>[];
  subColumns?: Column<Record<string, any>>[];
  expandSubRows?: boolean;
  data: Record<string, any>[];
  isFetching?: boolean;
  getSortBy?: boolean;
  manualSortBy?: boolean;
  isVisibleSubRowsHeaders?: boolean;
  showEmptyMsg?: boolean;
  emptyMsg?: string;
}

const getHiddenColumns = (data: readonly Column<Record<string, any>>[]) =>
  data.reduce((acc: string[], column: Record<string, any>) => {
    if (!column.isVisible) {
      acc.push(column.id);
    }
    return acc;
  }, []);

const TableRow: FC<TableRow> = ({ row, expandedRowClassname = '', expandedCellClassName = '' }) => {
  return (
    <tr
      {...row.getRowProps()}
      className={clsx('border-b text-lg leading-5', row.isExpanded ? expandedRowClassname : 'border-gray-20')}
    >
      {row.cells.map((cell) => {
        return (
          <td
            {...cell.getCellProps()}
            key={`table-data-${cell.column.id}`}
            className={clsx('px-3 py-4 break-all', expandedCellClassName)}
          >
            {cell.render('Cell')}
          </td>
        );
      })}
    </tr>
  );
};

const SubTable: FC<ReactTable> = ({ columns, data, isVisibleSubRowsHeaders }) => {
  const hiddenColumns = getHiddenColumns(columns);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: { hiddenColumns }
    },
    useFlexLayout
  );

  return (
    <table
      data-testid='activity-sub_table'
      {...getTableProps()}
      style={{ borderLeft: '1px solid #69CAEA', borderBottom: '2px solid #69CAEA' }}
      className='table-auto w-full'
    >
      {isVisibleSubRowsHeaders && (
        <thead className='border-b border-gray-20 bg-gray-75'>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={`table-row-${headerGroup.id}`} data-testid='sub_table-head'>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  key={`table-header-${column.id}`}
                  className='text-left font-bold text-lg leading-5 p-4 text-black'
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
      )}

      <tbody data-testid='activity-sub_table-body' {...getTableBodyProps()}>
        {rows.length &&
          rows.map((row) => {
            prepareRow(row);
            return <TableRow key={`table-row-${row.id}`} row={row} expandedCellClassName='bg-gray-5' />;
          })}
      </tbody>
    </table>
  );
};

export const ReactTable: FC<ReactTable> = ({
  columns,
  data,
  isFetching,
  getSortBy,
  manualSortBy = false,
  subColumns,
  expandSubRows = true,
  isVisibleSubRowsHeaders = true,
  showEmptyMsg = true,
  emptyMsg = 'No results found. Please try to adjust your search criteria.'
}) => {
  const { state: contextState, handlers } = useContext(ReactTableContext);
  const hiddenColumns = getHiddenColumns(columns);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = useTable(
    {
      columns,
      data,
      initialState: { ...contextState, hiddenColumns },
      manualSortBy,
      expandSubRows
    },
    useSortBy,
    useExpanded,
    useFlexLayout
  );

  useEffect(() => {
    if (getSortBy && !_.isEqual(contextState.sortBy[0], state.sortBy[0])) {
      handlers.setSortBy(state.sortBy);
    }
  }, [contextState.sortBy, getSortBy, handlers, state.sortBy]);

  return (
    <table data-testid='activity-table' className='table-auto w-full' {...getTableProps()}>
      <thead className='border-b border-gray-20'>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} data-testid='table-head'>
            {headerGroup.headers.map((column) => (
              <th
                className={clsx(
                  column.canSort && 'hover:text-gray-45',
                  'text-left font-bold text-lg leading-5 py-4 px-3 text-gray-30'
                )}
                {...column.getHeaderProps(column.getSortByToggleProps())}
              >
                {column.render('Header')}
                <span>
                  {column.canSort &&
                    (column.isSorted ? (
                      <CaretDown
                        className={clsx(
                          'w-5 h-5 transform inline ml-2 transition-transform duration-100',
                          column.isSortedDesc ? 'rotate-0' : 'rotate-180 '
                        )}
                      />
                    ) : (
                      <Carets className='inline w-5 h-5 ml-2' />
                    ))}
                </span>
              </th>
            ))}
          </tr>
        ))}
      </thead>

      <tbody data-testid='activity-table-body' {...getTableBodyProps()}>
        {isFetching ? (
          <tr className='mb-4 border-b border-gray-20 text-lg'>
            <td colSpan={12} className='px-3 py-4 col-span-6 text-center'>
              <PageSpinner />
            </td>
          </tr>
        ) : rows.length ? (
          rows.map((row) => {
            prepareRow(row);
            return (
              <Fragment key={`table-row-${row.id}`}>
                <TableRow row={row} expandedRowClassname='border-gray-20 bg-pale-blue' />
                {row.isExpanded && subColumns ? (
                  <tr {...row.getRowProps()}>
                    <td {...row.getRowProps()} className='p-0'>
                      <SubTable
                        columns={subColumns}
                        // @ts-ignore
                        data={row.original.subRows}
                        isVisibleSubRowsHeaders={isVisibleSubRowsHeaders}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })
        ) : (
          showEmptyMsg && (
            <tr className='mb-4 border-b border-gray-20 text-lg '>
              <td colSpan={12} className='px-3 py-4 col-span-12 text-center'>
                {emptyMsg}
              </td>
            </tr>
          )
        )}
      </tbody>
    </table>
  );
};
